import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { HardHat, Building2, Crown, ArrowRight, ArrowLeft, LogOut, Briefcase, ClipboardList, GraduationCap, ShieldCheck, ShieldAlert } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const CACHE_KEY_LOGO = 'login_logo_url';

export default function SeleccionarPerfil() {
  const navigate = useNavigate();
  const { applyPerfil, logout } = useAuth();
  const [perfiles, setPerfiles] = useState(null);
  const [logoImage, setLogoImage] = useState(() => localStorage.getItem(CACHE_KEY_LOGO));
  const [keyUserStep, setKeyUserStep] = useState(false); // true cuando se está eligiendo gerencia

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('perfiles_disponibles');
      if (!raw) {
        navigate('/login', { replace: true });
        return;
      }
      setPerfiles(JSON.parse(raw));
    } catch {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/configuracion`);
        if (res.data?.logo_image) {
          let imgUrl = res.data.logo_image;
          if (imgUrl.startsWith('/api/uploads/')) imgUrl = `${process.env.REACT_APP_BACKEND_URL}${imgUrl}`;
          setLogoImage(imgUrl);
          localStorage.setItem(CACHE_KEY_LOGO, imgUrl);
        }
      } catch { /* keep cached */ }
    })();
  }, []);

  const ingresar = (key, gerenciaSel = null) => {
    if (!perfiles?.[key]) return;
    const payload = perfiles[key];
    const userData = applyPerfil(payload);
    sessionStorage.removeItem('perfiles_disponibles');

    if (key === 'key_user' && gerenciaSel) {
      localStorage.setItem('active_gerencia', JSON.stringify(gerenciaSel));
    } else {
      localStorage.removeItem('active_gerencia');
    }

    toast.success(`¡Bienvenido, ${userData.nombre}!`);
    setTimeout(() => {
      if (key === 'ese') navigate('/ese/dashboard');
      else if (key === 'key_user') navigate('/key-user/documentos');
      else if (key === 'key_user_contratista') navigate('/key-user-empresa/documentos');
      else if (key === 'planificador') navigate('/liderazgo');
      else if (key === 'capacitador') navigate('/capacitador/dashboard');
      else if (key === 'pases_visita') navigate('/pases-visita');
      else if (key === 'equipo_hs') navigate('/equipo-hs/eventos');
      else navigate('/perfil-viewer');
    }, 50);
  };

  const onChooseKeyUser = () => {
    const gerencias = perfiles?.key_user?.user?.gerencias || [];
    if (gerencias.length === 1) {
      ingresar('key_user', gerencias[0]);
    } else {
      setKeyUserStep(true);
    }
  };

  const cancelar = () => {
    sessionStorage.removeItem('perfiles_disponibles');
    logout();
    navigate('/login', { replace: true });
  };

  if (!perfiles) return null;

  const trabajador = perfiles.trabajador?.user;
  const ese = perfiles.ese?.user;
  const keyUser = perfiles.key_user?.user;
  const keyUserContratista = perfiles.key_user_contratista?.user;
  const planificador = perfiles.planificador?.user;
  const capacitador = perfiles.capacitador?.user;
  const pasesVisita = perfiles.pases_visita?.user;
  const equipoHs = perfiles.equipo_hs?.user;
  const nombre = trabajador?.nombre || ese?.nombre || keyUser?.nombre || planificador?.nombre || capacitador?.nombre || pasesVisita?.nombre || equipoHs?.nombre;

  // ===== Paso 2: seleccionar gerencia (solo cuando >1) =====
  if (keyUserStep) {
    const gerencias = keyUser?.gerencias || [];
    return (
      <Layout logoImage={logoImage} setLogoImage={setLogoImage}>
        <header className="text-center mb-8">
          <button
            onClick={() => setKeyUserStep(false)}
            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 mb-4"
            data-testid="back-keyuser-step"
          >
            <ArrowLeft className="w-3 h-3" />
            Volver
          </button>
          <h1 className="text-2xl font-bold text-slate-900">Selecciona la Gerencia</h1>
          <p className="text-sm text-slate-500 mt-1">
            {nombre}, ingresarás como <span className="font-semibold text-amber-700">Key User</span> de la gerencia seleccionada.
          </p>
        </header>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
          {gerencias.map((g) => (
            <button
              key={g.id}
              onClick={() => ingresar('key_user', g)}
              data-testid={`select-gerencia-${g.id}`}
              className="group flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-4 text-left transition-all hover:shadow-md hover:scale-[1.02] hover:border-amber-300"
            >
              <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-amber-50 border-2 border-amber-300">
                <Briefcase className="w-5 h-5 text-amber-700" strokeWidth={2.2} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">{g.nombre}</p>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Gerencia</p>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-amber-700 group-hover:translate-x-0.5 transition-all" />
            </button>
          ))}
        </div>
        <div className="text-center mt-6">
          <button
            onClick={cancelar}
            className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-700"
            data-testid="cancelar-seleccion-step2"
          >
            <LogOut className="w-3 h-3" />
            Cancelar y volver al login
          </button>
        </div>
      </Layout>
    );
  }

  // ===== Paso 1: seleccionar perfil =====
  return (
    <Layout logoImage={logoImage} setLogoImage={setLogoImage}>
      <header className="text-center mb-8">
        <h1 className="text-2xl font-bold text-slate-900">Selecciona tu perfil</h1>
        <p className="text-sm text-slate-500 mt-1">
          Hola {nombre}, tu cuenta tiene acceso a varios perfiles. ¿Con cuál deseas ingresar?
        </p>
      </header>

      <div className="flex flex-wrap justify-center gap-4 max-w-5xl mx-auto">
        {trabajador && (
          <ProfileCard
            icon={HardHat}
            color="#2563EB"
            title="Perfil Trabajador"
            subtitle="Mis capacitaciones y módulos"
            description="Accede a tu perfil personal, cursos asignados, resultados y módulos TYSTA CORE habilitados."
            onClick={() => ingresar('trabajador')}
            testId="select-perfil-trabajador"
          />
        )}
        {keyUser && (
          <ProfileCard
            icon={Crown}
            color="#D97706"
            title="Perfil Key User"
            subtitle={(keyUser.gerencias?.length || 0) === 1 ? keyUser.gerencias[0].nombre : `${keyUser.gerencias?.length || 0} Gerencias`}
            description="Gestiona el organigrama y la documentación de tu(s) gerencia(s) asignada(s)."
            onClick={onChooseKeyUser}
            testId="select-perfil-keyuser"
          />
        )}
        {keyUserContratista && (
          <ProfileCard
            icon={Crown}
            color="#0F766E"
            title="Key User de mi Empresa"
            subtitle={keyUserContratista.empresa_nombre || 'Mi empresa'}
            description="Gestiona el organigrama y los documentos de tu empresa colaboradora."
            onClick={() => ingresar('key_user_contratista')}
            testId="select-perfil-keyuser-contratista"
          />
        )}
        {planificador && (
          <ProfileCard
            icon={ClipboardList}
            color="#DB2777"
            title="Perfil Planificador HS"
            subtitle="Actividades de liderazgo HSE"
            description="Planifica rondas, observaciones y compromisos de liderazgo visible."
            onClick={() => ingresar('planificador')}
            testId="select-perfil-planificador"
          />
        )}
        {capacitador && (
          <ProfileCard
            icon={GraduationCap}
            color="#DC2626"
            title="Perfil Capacitador"
            subtitle="Gestión de cursos y sesiones"
            description="Crea capacitaciones, agenda sesiones y evalúa resultados de los trabajadores."
            onClick={() => ingresar('capacitador')}
            testId="select-perfil-capacitador"
          />
        )}
        {ese && (
          <ProfileCard
            icon={Building2}
            color="#7C3AED"
            title="Perfil Gestión de Trabajadores"
            subtitle="Personal y Capacitaciones"
            description="Gestiona trabajadores, usuarios y sesiones de capacitación de tu empresa."
            onClick={() => ingresar('ese')}
            testId="select-perfil-ese"
          />
        )}
        {pasesVisita && (
          <ProfileCard
            icon={ShieldCheck}
            color="#059669"
            title="Perfil Inducción de Visitas"
            subtitle="Visualización de inducciones"
            description="Visualiza Inducciones aprobadas, vigentes y por vencer."
            onClick={() => ingresar('pases_visita')}
            testId="select-perfil-pases-visita"
          />
        )}
        {equipoHs && (
          <ProfileCard
            icon={ShieldAlert}
            color="#B91C1C"
            title="Perfil Equipo HS"
            subtitle="Revisión y aprobación de eventos"
            description="Gestiona eventos de incidentes, planes HS y notificaciones del equipo HSE."
            onClick={() => ingresar('equipo_hs')}
            testId="select-perfil-equipo-hs"
          />
        )}
      </div>

      <div className="text-center mt-6">
        <button
          onClick={cancelar}
          className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-700 transition-colors"
          data-testid="cancelar-seleccion"
        >
          <LogOut className="w-3 h-3" />
          Cancelar y volver al login
        </button>
      </div>
    </Layout>
  );
}

function Layout({ logoImage, setLogoImage, children }) {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-teal-50/40">
      <div className="flex-1 flex items-start justify-center p-4 pt-16">
        <div className="w-full max-w-5xl">
          {/* Logo Lomas Bayas centrado arriba */}
          <div className="flex justify-center mb-16" style={{ minHeight: '120px' }}>
            {logoImage && (
              <img
                src={logoImage}
                alt="Logo"
                className="max-h-28 w-auto object-contain"
                data-testid="seleccionar-logo"
                onError={() => { setLogoImage(null); localStorage.removeItem(CACHE_KEY_LOGO); }}
              />
            )}
          </div>
          {children}
        </div>
      </div>
      <footer className="py-4 px-6 text-center text-sm text-slate-400 border-t border-slate-200 bg-white/50">
        © TystaCore {new Date().getFullYear()}
      </footer>
    </div>
  );
}

function ProfileCard({ icon: Icon, color, title, subtitle, description, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="group relative bg-white border border-slate-200 rounded-2xl p-6 text-left transition-all hover:shadow-xl hover:scale-[1.03] w-full sm:w-[300px] overflow-hidden"
    >
      {/* Barra lateral de color */}
      <span className="absolute left-0 top-0 bottom-0 w-1.5" style={{ backgroundColor: color }} />
      {/* Glow de fondo del color */}
      <span
        className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-10 group-hover:opacity-20 transition-opacity"
        style={{ backgroundColor: color }}
      />
      <div className="relative flex items-start justify-between mb-4">
        <div className="w-14 h-14 rounded-xl flex items-center justify-center shadow-sm"
          style={{ backgroundColor: color, boxShadow: `0 4px 14px ${color}40` }}>
          <Icon className="w-7 h-7 text-white" strokeWidth={2.2} />
        </div>
        <ArrowRight className="w-4 h-4 text-slate-300 group-hover:translate-x-1 transition-all" style={{ color: undefined }} />
      </div>
      <h2 className="relative text-base font-bold text-slate-900">{title}</h2>
      <p className="relative text-xs uppercase tracking-wider mt-0.5 truncate font-semibold" style={{ color }}>{subtitle}</p>
      <p className="relative text-xs text-slate-500 mt-3 leading-relaxed">{description}</p>
    </button>
  );
}
